import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Tabs,
  Tab,
  Alert,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import UploadIcon from '@mui/icons-material/Upload';
import Papa from 'papaparse';
import api from '../axiosConfig';

const ClientImport = ({ setMessage, setMessageType }) => {
  const [activeTab,        setActiveTab]        = useState('INSTRUCTIONS');
  const [validationErrors, setValidationErrors] = useState([]);
  const [importResults,    setImportResults]    = useState(null);

  // ── Validation ─────────────────────────────────────────────────────────

  const validateDate = (dateStr) => {
    if (!dateStr) return false;
    const ukFormat = /^(\d{2})\/(\d{2})\/(\d{4})$/;
    if (!ukFormat.test(dateStr)) return false;
    const [, day, month, year] = dateStr.match(ukFormat);
    const d = new Date(year, month - 1, day);
    return (
      !isNaN(d.getTime()) &&
      d.getDate()      === parseInt(day)    &&
      d.getMonth() + 1 === parseInt(month)  &&
      d.getFullYear()  === parseInt(year)
    );
  };

  const VALID_PATCH_RESULTS = ['negative', 'positive', 'pending'];

  const validateRow = (data) => {
    const errors = [];

    if (!data.first_name?.trim()) errors.push('first_name is required');
    if (!data.phone?.trim())      errors.push('phone is required');

    const hasPtDate   = !!data.previous_patch_test_date?.trim();
    const hasPtResult = !!data.previous_patch_test_result?.trim();

    if (hasPtDate || hasPtResult) {
      if (hasPtDate && !validateDate(data.previous_patch_test_date.trim())) {
        errors.push('previous_patch_test_date must be DD/MM/YYYY');
      }
      if (hasPtResult && !VALID_PATCH_RESULTS.includes(data.previous_patch_test_result.trim().toLowerCase())) {
        errors.push(`previous_patch_test_result must be: ${VALID_PATCH_RESULTS.join(', ')}`);
      }
      if (hasPtDate && !hasPtResult) {
        errors.push('previous_patch_test_result is required when previous_patch_test_date is provided');
      }
      if (hasPtResult && !hasPtDate) {
        errors.push('previous_patch_test_date is required when previous_patch_test_result is provided');
      }
    }

    return errors;
  };

  // ── Handlers ───────────────────────────────────────────────────────────

  const handleTabChange = (event, newValue) => setActiveTab(newValue);

  const handleDownloadTemplate = () => {
    const headers = [
      'first_name', 'last_name', 'phone', 'email',
      'allergies', 'preferences', 'notes',
      'previous_patch_test_date', 'previous_patch_test_result',
      'previous_patch_test_product_type', 'previous_patch_test_product_brand',
      'previous_patch_test_product_mix', 'previous_patch_test_application_site',
    ].join(',');

    // Row 1: client without patch test history
    const row1 = ['Jane', 'Smith', '07712345678', 'jane@example.com', '', '', '', '', '', '', '', '', ''].join(',');

    // Row 2: client with patch test history
    const row2 = [
      'Sarah', 'Jones', '07798765432', '',
      'Sensitive scalp', '', '',
      '01/01/2026', 'negative',
      'Colour', 'Wella Koleston', '6/0 + 5/3 50/50 20vol', 'Behind left ear',
    ].join(',');

    const content = `${headers}\n${row1}\n${row2}`;
    const blob = new Blob([content], { type: 'text/csv' });
    const url  = window.URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = 'client_import_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setValidationErrors([]);
    setImportResults(null);

    Papa.parse(file, {
      header:        true,
      skipEmptyRows: true,
      complete: async (results) => {
        const allErrors = [];
        const validData = [];

        results.data.forEach((row, index) => {
          if (Object.values(row).every(v => !v)) return;

          const rowErrors = validateRow(row);
          if (rowErrors.length > 0) {
            allErrors.push(`Row ${index + 1}: ${rowErrors.join(', ')}`);
          } else {
            const processed = { ...row };
            if (processed.previous_patch_test_result) {
              processed.previous_patch_test_result =
                processed.previous_patch_test_result.trim().toLowerCase();
            }
            validData.push(processed);
          }
        });

        if (allErrors.length > 0) {
          setValidationErrors(allErrors);
          setMessage('Validation errors found. Please check the errors below and try again.');
          setMessageType('error');
          return;
        }

        try {
          const response = await api.post('/pro-booking/clients/import', validData);
          const res = response.data;
          setImportResults(res);

          if (res.successful_imports === 0 && res.skipped_duplicates === 0) {
            setMessageType('error');
            if (res.errors?.length > 0) {
              setValidationErrors(res.errors);
              setMessage('Import failed. Please check the errors below.');
            } else {
              setMessage('Import failed. No clients were imported.');
            }
          } else if (res.failed_imports > 0) {
            setMessageType('warning');
            setMessage(
              `Imported ${res.successful_imports} clients` +
              (res.skipped_duplicates > 0 ? `, ${res.skipped_duplicates} duplicates skipped` : '') +
              `, ${res.failed_imports} failed.`
            );
            if (res.errors?.length > 0) setValidationErrors(res.errors);
          } else {
            setMessageType('success');
            setMessage(
              `Successfully imported ${res.successful_imports} clients` +
              (res.skipped_duplicates > 0 ? ` (${res.skipped_duplicates} duplicates skipped)` : '') +
              '.'
            );
          }

          event.target.value = '';

        } catch (error) {
          setMessageType('error');
          if (error.response?.data?.errors) {
            setValidationErrors(error.response.data.errors);
            setMessage('Import failed. Please check the errors below.');
          } else {
            setMessage('Error importing clients. Please try again.');
          }
        }
      },
      error: () => {
        setMessage('Error reading CSV file. Please check the format and try again.');
        setMessageType('error');
      },
    });
  };

  // ── Render ─────────────────────────────────────────────────────────────

  return (
    <Paper elevation={3} sx={{ p: 3, maxWidth: 1200, margin: '0 auto' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">Client Import</Typography>
      </Box>

      <Box sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange}
          sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab label="Import Instructions" value="INSTRUCTIONS" />
          <Tab label="Import"              value="IMPORT" />
        </Tabs>
      </Box>

      {/* ── Instructions tab ── */}
      {activeTab === 'INSTRUCTIONS' && (
        <Box>
          <Typography variant="h6" gutterBottom>Before You Import</Typography>

          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="subtitle2">Required fields:</Typography>
            <ul>
              <li><strong>first_name</strong> — client's first name</li>
              <li><strong>phone</strong> — used for duplicate detection; clients with a matching phone number will be skipped</li>
            </ul>
            <Typography variant="subtitle2" sx={{ mt: 1 }}>Optional fields:</Typography>
            <ul>
              <li>last_name, email, allergies, preferences, notes</li>
            </ul>
          </Alert>

          <Alert severity="warning" sx={{ mb: 3 }}>
            <Typography variant="subtitle2">
              Previous patch test records (migrating salons only):
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              If you have existing paper patch test records, you can import them using these
              optional columns. This is the <strong>only</strong> legitimate way to bring
              pre-system patch test history into SalonLogic — there is no manual override
              on live bookings.
            </Typography>
            <ul>
              <li><strong>previous_patch_test_date</strong> — DD/MM/YYYY format</li>
              <li><strong>previous_patch_test_result</strong> — must be exactly: <code>negative</code>, <code>positive</code>, or <code>pending</code></li>
              <li>previous_patch_test_product_type (optional)</li>
              <li>previous_patch_test_product_brand (optional)</li>
              <li>previous_patch_test_product_mix (optional, e.g. "6/0 + 5/3 50/50 20vol")</li>
              <li>previous_patch_test_application_site (optional, e.g. "Behind left ear")</li>
            </ul>
            <Typography variant="body2" sx={{ mt: 1 }}>
              Both date and result must be provided together or not at all.
            </Typography>
          </Alert>

          <Alert severity="error" sx={{ mb: 3 }}>
            <Typography variant="subtitle2">Important — patch test expiry:</Typography>
            <Typography variant="body2">
              Negative patch test results expire after <strong>6 months</strong> from the test date.
              Records older than 6 months will be stored as history but will show as{' '}
              <strong>Expired</strong> — the client will need a new patch test before colour
              services can be confirmed. The standard 48-hour rule also still applies to
              imported records.
            </Typography>
          </Alert>
        </Box>
      )}

      {/* ── Import tab ── */}
      {activeTab === 'IMPORT' && (
        <Box>
          <Typography variant="subtitle1" gutterBottom>Import Instructions</Typography>
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              1. Download the template below
              <br />
              2. Fill in your client data — first_name and phone are required per row
              <br />
              3. Add previous patch test columns only if migrating existing paper records
              <br />
              4. Save and upload your completed CSV file
            </Typography>
          </Alert>

          {validationErrors.length > 0 && (
            <Alert severity="error" sx={{ mb: 3 }}>
              <Typography variant="subtitle2">Validation Errors:</Typography>
              <ul>
                {validationErrors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </Alert>
          )}

          {importResults && (
            <Alert
              severity={
                importResults.successful_imports > 0
                  ? importResults.failed_imports > 0 ? 'warning' : 'success'
                  : 'error'
              }
              sx={{ mb: 3 }}
            >
              <Typography variant="subtitle2">Import Results:</Typography>
              <ul>
                <li>Successfully imported: {importResults.successful_imports}</li>
                {importResults.skipped_duplicates > 0 && (
                  <li>Duplicates skipped (phone already on file): {importResults.skipped_duplicates}</li>
                )}
                {importResults.failed_imports > 0 && (
                  <li>Failed: {importResults.failed_imports}</li>
                )}
              </ul>
            </Alert>
          )}

          <Button
            startIcon={<DownloadIcon />}
            variant="outlined"
            onClick={handleDownloadTemplate}
            sx={{ mt: 2, mb: 4 }}
          >
            DOWNLOAD TEMPLATE
          </Button>

          <Box>
            <Typography variant="subtitle1" gutterBottom>Import Data</Typography>
            <input
              type="file"
              accept=".csv"
              onChange={handleFileUpload}
              style={{ display: 'none' }}
              id="client-csv-file-input"
            />
            <label htmlFor="client-csv-file-input">
              <Button startIcon={<UploadIcon />} variant="contained" component="span">
                Choose CSV File
              </Button>
            </label>
          </Box>
        </Box>
      )}
    </Paper>
  );
};

export default ClientImport;